import { useState, useCallback } from 'react'
import { generateTollRoutes, RouteAlternative } from '../api/tollRoutes.api'

interface LocationInfo {
    lat: number
    lng: number
    city: string
    state: string
}

interface UseTollRoutesProps {
    origin: LocationInfo
    destination: LocationInfo
    hasFinalCoords: boolean
}

export interface UseTollRoutesReturn {
    tollRoutes: RouteAlternative[]
    selectedRouteIndex: number
    isTollLoading: boolean
    tollsFetched: boolean
    showTollBoxes: boolean
    tollError: string | null
    handleGetTolls: (e: React.MouseEvent) => Promise<void>
    handleTollBoxClick: (index: number) => void
    hideTollBoxes: () => void
}

export const useTollRoutes = ({ origin, destination, hasFinalCoords }: UseTollRoutesProps): UseTollRoutesReturn => {
    const [tollRoutes, setTollRoutes] = useState<RouteAlternative[]>([])
    const [selectedRouteIndex, setSelectedRouteIndex] = useState(0)
    const [isTollLoading, setIsTollLoading] = useState(false)
    const [tollsFetched, setTollsFetched] = useState(false)
    const [showTollBoxes, setShowTollBoxes] = useState(false)
    const [tollError, setTollError] = useState<string | null>(null)

    const handleGetTolls = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation()

            if (!hasFinalCoords) {
                setTollError('Missing coordinates')
                return
            }

            // If already fetched, just toggle visibility
            if (tollsFetched) {
                setShowTollBoxes(true)
                return
            }

            setIsTollLoading(true)
            setTollError(null)

            try {
                const response = await generateTollRoutes({
                    origin: { lat: origin.lat, lng: origin.lng, city: origin.city, state: origin.state },
                    destination: { lat: destination.lat, lng: destination.lng, city: destination.city, state: destination.state },
                    maxAlternatives: 4,
                })

                if (response.routes && response.routes.length > 0) {
                    setTollRoutes(response.routes)
                    setShowTollBoxes(true)
                    const recommendedIndex = response.routes.findIndex((r) => r.isRecommended)
                    setSelectedRouteIndex(recommendedIndex >= 0 ? recommendedIndex : 0)
                } else {
                    setTollError('No routes found')
                }
                setTollsFetched(true)
            } catch {
                setTollError('Failed to load tolls')
                setTollsFetched(true)
            } finally {
                setIsTollLoading(false)
            }
        },
        [hasFinalCoords, tollsFetched, origin, destination]
    )

    const handleTollBoxClick = useCallback((index: number) => {
        setSelectedRouteIndex(index)
    }, [])

    const hideTollBoxes = useCallback(() => {
        setShowTollBoxes(false)
    }, [])

    return {
        tollRoutes,
        selectedRouteIndex,
        isTollLoading,
        tollsFetched,
        showTollBoxes,
        tollError,
        handleGetTolls,
        handleTollBoxClick,
        hideTollBoxes,
    }
}
